// Synth Lab v2: the concept ladder and its exercise generators.
//
// V2 replaces the old six-parallel-modes page with one spine: each CONCEPT
// (waveform, cutoff, the envelope stages, resonance) has a ladder of RUNGS
// (hear → name → which-way → dial → fix), and every practice round is an
// exercise generated here for a specific (concept, rung).
//
// The core honesty rule lives here too: a round is never generated that the
// player can't hear. Each concept has an audibility GUARD that constrains the
// context patch (e.g. no cutoff questions on a sine wave, no sustain questions
// with a decay longer than the preview gate) and keeps test material out of
// the sub-bass where phone speakers give up.
//
// Pure data + pure functions (no audio, no React, no storage) — the page owns
// playback and persistence; this module is fully unit-testable.

import {
  PARAMS,
  WAVEFORM_VALUES,
  PRESETS,
  LESSONS,
  clamp,
  normalizeParam,
  denormalizeParam,
} from "./synthLab";
import { DIRECTION_LABELS, conceptLabel } from "./synthDrills";

export { conceptLabel };

/* ============================================================
   Concepts + rungs — the shape of the journey
   ============================================================ */

// Teaching order: broad-strokes tone first, envelope stages in rising subtlety,
// resonance last (it only makes sense once cutoff is understood).
export const CONCEPT_ORDER = [
  "waveform",
  "cutoff",
  "attack",
  "release",
  "sustain",
  "decay",
  "resonance",
];

export const RUNGS = [
  { id: "hear", label: "Hear it" },
  { id: "name", label: "Name it" },
  { id: "way", label: "Which way" },
  { id: "dial", label: "Dial it" },
  { id: "fix", label: "Fix it" },
];

export const RUNG_BY_ID = RUNGS.reduce((m, r) => {
  m[r.id] = r;
  return m;
}, {});

// Waveform has no up/down, so its ladder skips the "which way" rung.
export const conceptRungIds = (conceptId) =>
  conceptId === "waveform"
    ? ["hear", "name", "dial", "fix"]
    : ["hear", "name", "way", "dial", "fix"];

// Which /synth-lab/learn lesson teaches a concept (decay + sustain share one).
export const LESSON_FOR_CONCEPT = {
  waveform: "waveform",
  cutoff: "cutoff",
  resonance: "resonance",
  attack: "attack",
  decay: "decay",
  sustain: "decay",
  release: "release",
};

export const lessonForConcept = (conceptId) =>
  LESSONS.find((l) => l.id === LESSON_FOR_CONCEPT[conceptId]) || null;

/* ============================================================
   Seeded rng — sessions are reproducible under a seed
   ============================================================ */

// mulberry32 over a string hash. Good enough spread for exercise variety, and
// deterministic so a "daily" seed yields the same session everywhere.
export const seededRng = (seed) => {
  let h = 1779033703 ^ String(seed).length;
  for (let i = 0; i < String(seed).length; i++) {
    h = Math.imul(h ^ String(seed).charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pickFrom = (arr, rng) => arr[Math.floor(rng() * arr.length)];

const shuffle = (arr, rng) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = arr[i];
    arr[i] = arr[j];
    arr[j] = t;
  }
  return arr;
};

/* ============================================================
   Audibility guards — never ask about a change you can't hear
   ============================================================ */

const ALL_WAVES = [...WAVEFORM_VALUES];
const RICH_WAVES = ["sawtooth", "square", "triangle"]; // harmonics to filter
const BUZZY_WAVES = ["sawtooth", "square"]; // strong harmonics for resonance

// Per focus-concept constraints on the CONTEXT patch (every param that isn't
// the one being tested), expressed in normalized 0..1 units, plus the playable
// note range (MIDI) and the normalized `range` the focus param may move in.
// Rationale per entry:
//  - cutoff/resonance need harmonics above the fundamental to act on;
//  - decay is masked when sustain is high (nothing to fall to);
//  - sustain needs a short decay so the held level is reached within the gate;
//  - attack/release need enough sustain level for the ramp/tail to be audible;
//  - note ranges stay at/above ~C3 so small phone speakers can reproduce them.
export const GUARDS = {
  cutoff: {
    waveforms: RICH_WAVES,
    note: [48, 67],
    fixed: { resonance: [0, 0.5], attack: [0, 0.35], decay: [0.2, 0.8], sustain: [0.4, 1], release: [0.1, 0.6] },
    range: [0.18, 0.85],
  },
  resonance: {
    waveforms: BUZZY_WAVES,
    note: [48, 67],
    fixed: { cutoff: [0.35, 0.6], attack: [0, 0.3], decay: [0.2, 0.8], sustain: [0.4, 1], release: [0.1, 0.6] },
    range: [0.05, 0.95],
  },
  attack: {
    waveforms: ALL_WAVES,
    note: [52, 72],
    fixed: { cutoff: [0.45, 0.9], resonance: [0, 0.5], decay: [0.3, 0.8], sustain: [0.5, 1], release: [0.1, 0.5] },
    range: [0, 0.8],
  },
  decay: {
    waveforms: ALL_WAVES,
    note: [52, 72],
    fixed: { cutoff: [0.45, 0.9], resonance: [0, 0.5], attack: [0, 0.15], sustain: [0.1, 0.45], release: [0.1, 0.5] },
    range: [0.15, 0.8],
  },
  sustain: {
    waveforms: ALL_WAVES,
    note: [52, 72],
    fixed: { cutoff: [0.45, 0.9], resonance: [0, 0.5], attack: [0, 0.15], decay: [0.15, 0.4], release: [0.1, 0.5] },
    range: [0.05, 0.95],
  },
  release: {
    waveforms: ALL_WAVES,
    note: [52, 72],
    fixed: { cutoff: [0.45, 0.9], resonance: [0, 0.5], attack: [0, 0.15], decay: [0.2, 0.6], sustain: [0.5, 1] },
    range: [0.05, 0.9],
  },
  waveform: {
    waveforms: ALL_WAVES,
    note: [52, 72],
    fixed: { cutoff: [0.6, 0.95], resonance: [0, 0.4], attack: [0, 0.2], decay: [0.3, 0.8], sustain: [0.5, 1], release: [0.1, 0.5] },
    range: null,
  },
};

// Preview gate per focus concept (ms). Slow-envelope concepts get a longer
// held note so their stage is actually reached; release keeps a short gate so
// the tail after letting go is the star.
const GATES = { attack: 1100, decay: 1100, sustain: 1300, release: 700 };
export const gateFor = (conceptId) => GATES[conceptId] || 900;

// A context patch that keeps every non-focus param inside the focus concept's
// audible band. The focus param itself gets a placeholder mid-range value that
// generators then overwrite.
export const contextPatch = (focusId, rng = Math.random) => {
  const guard = GUARDS[focusId];
  const patch = { waveform: pickFrom(guard.waveforms, rng) };
  PARAMS.forEach((p) => {
    const band = guard.fixed[p.id];
    const t = band ? band[0] + rng() * (band[1] - band[0]) : 0.5;
    patch[p.id] = denormalizeParam(p.id, t);
  });
  return patch;
};

export const pickNote = (focusId, rng = Math.random) => {
  const [lo, hi] = GUARDS[focusId].note;
  return lo + Math.floor(rng() * (hi - lo + 1));
};

/* ============================================================
   Difficulty — shift size shrinks as stars grow
   ============================================================ */

// Normalized shift magnitude bands per level (1 = new, 3 = nearly mastered).
const LEVEL_SHIFT = {
  1: [0.5, 0.7],
  2: [0.35, 0.5],
  3: [0.22, 0.35],
};

// Place a from→to move for the focus param entirely inside its audible range:
// sample a magnitude for the level (capped to the range width), a start that
// leaves room, and a random direction.
const placeShift = (focusId, level, rng) => {
  const [lo, hi] = GUARDS[focusId].range;
  const band = LEVEL_SHIFT[clamp(level, 1, 3)] || LEVEL_SHIFT[1];
  const width = hi - lo;
  const mag = Math.min(band[0] + rng() * (band[1] - band[0]), width * 0.9);
  const start = lo + rng() * (width - mag);
  const up = rng() < 0.5;
  const fromN = up ? start : start + mag;
  const toN = up ? start + mag : start;
  return {
    from: denormalizeParam(focusId, fromN),
    to: denormalizeParam(focusId, toN),
    direction: toN > fromN ? "up" : "down",
  };
};

/* ============================================================
   Exercise generators — one per rung
   ============================================================ */

// "Hear it": the lesson content, presented inside the practice player.
export const makeHearRound = (conceptId) => ({
  kind: "hear",
  concept: conceptId,
  lesson: lessonForConcept(conceptId),
});

// "Name it": A/B, one control apart — which control changed?
//
// Only TAUGHT (unlocked) concepts ever appear by name: the player can't be
// asked to name a control no lesson has introduced. Everything untaught
// collapses into one "Something else" choice (OTHER_ID), and sometimes the
// changed control genuinely IS an untaught one — so even on day one, with a
// single taught concept, the round is real discrimination ("did the waveform
// change, or something else?") rather than a constant answer. The OTHER
// choice is shown whenever untaught concepts remain, never only when it's
// correct, so its presence can't leak the answer. Results are recorded
// against the rung's concept either way.
export const OTHER_ID = "__other";

export const nameChoiceLabel = (id) => (id === OTHER_ID ? "Something else" : conceptLabel(id));

export const makeNameRound = (conceptId, { level = 1, unlocked = [conceptId], rng = Math.random } = {}) => {
  const taught = CONCEPT_ORDER.filter((c) => unlocked.includes(c));
  const untaught = CONCEPT_ORDER.filter((c) => !taught.includes(c));

  // Pick what actually moves: mostly the rung's concept, sometimes another
  // taught one, and (while any remain) sometimes an untaught mystery control.
  // A lone taught concept leans harder on mystery rounds — the alternative is
  // an always-identical answer.
  const pOther = !untaught.length ? 0 : taught.length === 1 ? 0.45 : 0.25;
  const roll = rng();
  let changed;
  if (roll < pOther) {
    changed = pickFrom(untaught, rng);
  } else {
    const others = taught.filter((c) => c !== conceptId);
    changed = others.length && rng() > 0.6 ? pickFrom(others, rng) : conceptId;
  }
  const answerId = taught.includes(changed) ? changed : OTHER_ID;

  const a = contextPatch(changed, rng);
  const b = { ...a };
  if (changed === "waveform") {
    b.waveform = pickFrom(GUARDS.waveform.waveforms.filter((w) => w !== a.waveform), rng);
  } else {
    const move = placeShift(changed, level, rng);
    a[changed] = move.from;
    b[changed] = move.to;
  }

  // Choices: the focus concept and (if taught) the changed one, padded with
  // taught distractors — plus OTHER whenever anything is still untaught.
  const taughtSlots = untaught.length ? 3 : 4;
  const choiceSet = [conceptId];
  if (answerId !== OTHER_ID && answerId !== conceptId) choiceSet.push(answerId);
  const distractors = shuffle(taught.filter((c) => !choiceSet.includes(c)), rng);
  while (choiceSet.length < Math.min(taughtSlots, taught.length) && distractors.length) {
    choiceSet.push(distractors.pop());
  }
  const choices = shuffle(choiceSet, rng);
  if (untaught.length) choices.push(OTHER_ID); // fixed last slot, easy to find

  return {
    kind: "name",
    concept: conceptId,
    changed,
    answerId,
    a,
    b,
    note: pickNote(changed, rng),
    gate: gateFor(changed),
    choices,
    level,
  };
};

// "Which way": the concept is named, the player calls the direction using its
// musical labels ("Brighter"/"Darker"). Never generated for waveform.
export const makeWayRound = (conceptId, { level = 1, rng = Math.random } = {}) => {
  const a = contextPatch(conceptId, rng);
  const move = placeShift(conceptId, level, rng);
  a[conceptId] = move.from;
  const b = { ...a, [conceptId]: move.to };
  return {
    kind: "way",
    concept: conceptId,
    a,
    b,
    note: pickNote(conceptId, rng),
    gate: gateFor(conceptId),
    direction: move.direction,
    labels: DIRECTION_LABELS[conceptId],
    level,
  };
};

// "Dial it": hear a hidden target, then set the one knob by ear. For waveform
// the "knob" is the four-wave picker. `start` parks the player's knob well
// away from the target so there's always a real move to make.
export const DIAL_TOLERANCE = 0.12;
export const DIAL_CHECKS = 3;

export const makeDialRound = (conceptId, { level = 1, rng = Math.random } = {}) => {
  const context = contextPatch(conceptId, rng);
  if (conceptId === "waveform") {
    const target = pickFrom(GUARDS.waveform.waveforms, rng);
    const start = pickFrom(GUARDS.waveform.waveforms.filter((w) => w !== target), rng);
    return {
      kind: "dial",
      concept: conceptId,
      context,
      targetValue: target,
      startValue: start,
      note: pickNote(conceptId, rng),
      gate: gateFor(conceptId),
      checksAllowed: DIAL_CHECKS,
      level,
    };
  }
  const [lo, hi] = GUARDS[conceptId].range;
  const targetN = lo + rng() * (hi - lo);
  // Start at least 0.3 away (clamped into range) so "do nothing" never passes.
  const room = 0.3;
  const below = targetN - lo >= room;
  const above = hi - targetN >= room;
  const goBelow = below && above ? rng() < 0.5 : below;
  const startN = goBelow
    ? lo + rng() * (targetN - room - lo)
    : targetN + room + rng() * (hi - (targetN + room));
  return {
    kind: "dial",
    concept: conceptId,
    context,
    targetValue: denormalizeParam(conceptId, targetN),
    startValue: denormalizeParam(conceptId, clamp(startN, lo, hi)),
    note: pickNote(conceptId, rng),
    gate: gateFor(conceptId),
    checksAllowed: DIAL_CHECKS,
    level,
  };
};

// Grade a dial guess against the target: pass inside the tolerance band, and
// otherwise a musically-worded nudge ("a touch brighter", "a lot shorter").
export const dialGrade = (conceptId, guessValue, targetValue) => {
  if (conceptId === "waveform") {
    const pass = guessValue === targetValue;
    return { pass, delta: pass ? 0 : 1, hint: pass ? null : "not this wave — listen for the buzz" };
  }
  const delta = normalizeParam(conceptId, guessValue) - normalizeParam(conceptId, targetValue);
  const pass = Math.abs(delta) <= DIAL_TOLERANCE;
  if (pass) return { pass, delta, hint: null };
  const dirWord = DIRECTION_LABELS[conceptId][delta < 0 ? "up" : "down"].toLowerCase();
  return { pass, delta, hint: `${Math.abs(delta) > 0.3 ? "a lot" : "a touch"} ${dirWord}` };
};

/* ============================================================
   "Fix it" — a preset with one control wrecked
   ============================================================ */

// Whether wrecking `conceptId` on this preset would be audible. Lighter than
// the full GUARDS (presets are hand-tuned and play with a generous gate) —
// these check only the interactions that can genuinely mask a change.
export const canWreck = (preset, conceptId) => {
  const p = preset.patch;
  if (conceptId === "cutoff") return RICH_WAVES.includes(p.waveform);
  if (conceptId === "resonance") return BUZZY_WAVES.includes(p.waveform);
  if (conceptId === "decay") return clamp(p.sustain, 0, 1) <= 0.7;
  if (conceptId === "sustain") return p.decay <= 1.2;
  return true; // waveform / attack / release are audible on every preset
};

export const wreckablePresets = (conceptId) => PRESETS.filter((pr) => canWreck(pr, conceptId));

const FIX_GATE = 1300;
const FIX_SHIFT = [0.4, 0.6];

// Build a Fix-the-Patch round: a real preset (falling back to a generated
// "mystery patch" if none is eligible) with `conceptId` knocked far off. When
// `reveal` is false the player must first identify WHICH control is off —
// the two-stage version used as a session capstone.
export const makeFixRound = (conceptId, { rng = Math.random, reveal = true, unlocked = CONCEPT_ORDER } = {}) => {
  const eligible = wreckablePresets(conceptId);
  const preset = eligible.length
    ? pickFrom(eligible, rng)
    : { name: "Mystery patch", emoji: "🎛️", patch: contextPatch(conceptId, rng) };
  const target = { ...preset.patch };
  const start = { ...target };
  if (conceptId === "waveform") {
    start.waveform = pickFrom(WAVEFORM_VALUES.filter((w) => w !== target.waveform), rng);
  } else {
    const n = normalizeParam(conceptId, target[conceptId]);
    const mag = FIX_SHIFT[0] + rng() * (FIX_SHIFT[1] - FIX_SHIFT[0]);
    const dir = n > 0.5 ? -1 : 1;
    start[conceptId] = denormalizeParam(conceptId, clamp(n + dir * mag, 0.02, 0.98));
  }
  const distractors = shuffle(unlocked.filter((c) => c !== conceptId), rng);
  const choices = shuffle([conceptId, ...distractors.slice(0, 3)], rng);
  return {
    kind: "fix",
    concept: conceptId,
    presetName: preset.name,
    presetEmoji: preset.emoji,
    target,
    start,
    note: pickNote(conceptId, rng),
    gate: FIX_GATE,
    reveal,
    choices,
    checksAllowed: DIAL_CHECKS,
  };
};

/* ============================================================
   Descriptor → exercise
   ============================================================ */

// The session scheduler (synthMastery.js) emits {concept, rung, level}
// descriptors; this turns one into a concrete playable exercise.
export const makeExercise = (descriptor, { rng = Math.random, unlocked = CONCEPT_ORDER } = {}) => {
  const { concept, rung, level = 1, reveal } = descriptor;
  switch (rung) {
    case "hear":
      return makeHearRound(concept);
    case "name":
      return makeNameRound(concept, { level, unlocked, rng });
    case "way":
      return makeWayRound(concept, { level, rng });
    case "dial":
      return makeDialRound(concept, { level, rng });
    case "fix":
      return makeFixRound(concept, { rng, reveal: reveal !== false, unlocked });
    default:
      return makeHearRound(concept);
  }
};
